import { EXPANSION_PROVIDERS, expansionBenefitKey, connectExpansion, disconnectExpansion, expansionSession, expansionRequest, type ExpansionProvider } from "@/lib/expansion";
import { connectPeopleTasks, invokePeopleTask, nativePeopleTasksAvailable, type PeopleTaskProvider } from "@/lib/peopleTasks";
import { PeopleTaskActionModal, type PeopleTaskPrefill } from "@/components/mirror/PeopleTaskActionModal";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import type { EmailActionPrefill, EmailProvider } from "@/components/mirror/EmailActionModal";
import { fileOwnerSession } from "@/lib/fileExecutor";
import { connectedFileReadSessionId } from "@/lib/connectedFileRead";
import type { FileActionPrefill, FileProvider } from "@/components/mirror/FileActionModal";
import type { ContactActionPrefill } from "@/lib/contactExecutor";
import type { ExternalTaskActionPrefill } from "@/lib/externalTaskExecutor";
import MicrosoftTodoImportLauncher from "@/components/connections/MicrosoftTodoImportLauncher";
const ExternalTaskActionModal = lazy(() => import("@/components/mirror/ExternalTaskActionModal").then(m => ({default:m.ExternalTaskActionModal})));
const EmailActionModal = lazy(() => import("@/components/mirror/EmailActionModal").then(m => ({ default: m.EmailActionModal })));
const FileActionModal = lazy(() => import("@/components/mirror/FileActionModal").then(m => ({ default: m.FileActionModal })));
const ContactActionModal = lazy(() => import("@/components/mirror/ContactActionModal").then(m => ({ default: m.ContactActionModal })));
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, MotionConfig } from "framer-motion";
import { ChevronLeft, Activity, Loader2, Calendar, CalendarClock, CalendarDays, ListTodo, HeartPulse, FileText, Music, RefreshCw, ArrowUpRight, Search } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useConnections, platformHealthProvider } from "@/hooks/useConnections";
import { useLanguage } from "@/i18n/LanguageProvider";
import { connectionGroups, connectionNeedsAttention } from "@/lib/connectionOverview";
import ConnectionCard from "@/components/connections/ConnectionCard";
import ConnectionHistoryPanel from "@/components/connections/ConnectionHistoryPanel";
import { stravaHistoryForAthlete } from "@/lib/connectionHistoryResult";
import PreparedConnectionCard from "@/components/connections/PreparedConnectionCard";
const ConnectedFileEditWorkspace = lazy(() => import("@/components/connections/ConnectedFileEditWorkspace").then(m => ({default:m.ConnectedFileEditWorkspace})));
const CreatorConnections = lazy(() => import("@/components/connections/CreatorConnections"));
import { WhatsAppLinkCardView } from "@/components/connections/WhatsAppLinkCard";
import { ConnectionBrandInline } from "@/components/connections/ConnectionBrandMark";
import { useWhatsAppLink } from "@/hooks/useWhatsAppLink";
import {
  INTEGRATIONS,
  integrationsByCategory,
  type IntegrationConfig,
  type IntegrationCategory,
} from "@/config/integrations";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { useEmailConnectionAccess } from "@/hooks/useEmailConnectionAccess";
import { usePeopleTaskConnectionAccess } from "@/hooks/usePeopleTaskConnectionAccess";
import { useFileConnectionAccess } from "@/hooks/useFileConnectionAccess";
import { useExpansionConnectionAccess } from "@/hooks/useExpansionConnectionAccess";
import { capacitorErrorMessage, isPluginUnimplemented } from "@/lib/capacitorErrors";
import { HEALTH_TYPE_LABEL_KEY, deniedFromMetadata } from "@/lib/health/permissionState";

// Section headers driven by category. Order = render order.
const SECTIONS: { category: IntegrationCategory; headerKey: string }[] = [
  { category: "active", headerKey: "settings.integrations.section.active" },
  { category: "calendar_productivity", headerKey: "settings.integrations.section.calendarProductivity" },
  { category: "wearables_health", headerKey: "settings.integrations.section.wearablesHealth" },
];

export default function SettingsConnections() {
  const navigate = useNavigate();
  const { providers: expansionProviders, dropboxEnabled, todoistEnabled, trelloEnabled, slackEnabled, microsoftTeamsEnabled, userId: expansionOwner } = useExpansionConnectionAccess();
  const { gmailEnabled, outlookEnabled } = useEmailConnectionAccess();
  const { googleDriveEnabled, oneDriveEnabled, filesEnabled, identityKey: fileOwner } = useFileConnectionAccess();
  const { googleContactsEnabled, microsoftContactsEnabled, microsoftTodoEnabled, googleTasksEnabled, userId: peopleOwner } = usePeopleTaskConnectionAccess();
  const [emailPrefill, setEmailPrefill] = useState<EmailActionPrefill | null>(null);
  const [fileEditOpen, setFileEditOpen] = useState(false);
  const [filePrefill, setFilePrefill] = useState<FileActionPrefill | null>(null);
  const [fileOrigin, setFileOrigin] = useState<{ ownerId: string; authSessionId: string } | null>(null);
  const [fileReaderOpening, setFileReaderOpening] = useState(false);
  const fileReaderLock = useRef(false), fileReaderSource = useRef("");
  const [peoplePrefill, setPeoplePrefill] = useState<PeopleTaskPrefill | null>(null);
  const [externalTaskPrefill, setExternalTaskPrefill] = useState<ExternalTaskActionPrefill | null>(null);
  const [contactPrefill, setContactPrefill] = useState<ContactActionPrefill | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, tf } = useLanguage();
  const [syncProvider, setSyncProvider] = useState<string | null>(null);
  const [catalogQuery, setCatalogQuery] = useState("");
  const whatsappLink = useWhatsAppLink();
  const syncLock = useRef(false);
  const runSync = async (provider: string, action: () => Promise<void>) => {
    if (syncLock.current) return; syncLock.current = true; setSyncProvider(provider);
    try { await action(); } finally { syncLock.current = false; setSyncProvider(null); }
  };
  const {
    connections,
    connectionOwner,
    stravaConnection,
    stravaActivityCount,
    connectionsError,
    googleCalConnection,
    loading,
    syncing,
    connectingProvider,
    connectStrava,
    syncStrava,
    disconnectStrava,
    connectGoogleCal,
    syncGoogleCal,
    disconnectGoogleCal,
    gmailConnection,
    connectGmail,
    syncGmail,
    disconnectGmail,
    googleDriveConnection,
    connectGoogleDrive,
    syncGoogleDrive,
    disconnectGoogleDrive,
    outlookConnection,
    connectOutlook,
    syncOutlook,
    disconnectOutlook,
    outlookMailConnection,
    connectOutlookMail,
    syncOutlookMail,
    disconnectOutlookMail,
    oneDriveConnection,
    connectOneDrive,
    syncOneDrive,
    disconnectOneDrive,
    googleTasksConnection,
    googleTasksCount,
    connectGoogleTasks,
    syncGoogleTasks,
    disconnectGoogleTasks,
    appleCalConnection,
    connectAppleCal,
    syncAppleCal,
    disconnectAppleCal,
    healthConnection,
    healthItemCount,
    connectHealth,
    syncHealth,
    disconnectHealth,
    fixHealthPermissions,
    notionConnection,
    notionItemCount,
    connectNotion,
    syncNotion,
    disconnectNotion,
    spotifyConnection,
    spotifyItemCount,
    connectSpotify,
    syncSpotify,
    disconnectSpotify,
    refresh,
  } = useConnections();

  // Apple Calendar (EventKit) is on-device, iOS-only — hide the card on web
  // and Android where requestFullCalendarAccess/listEventsInRange aren't bridged.
  const isIos = Capacitor.getPlatform() === "ios";

  // Saúde nativa: Apple Saúde no iOS, Health Connect no Android. Não existe no
  // navegador, e mostrar um card que não dá para conectar é pior que não
  // mostrar — por isso o card só aparece quando há provider de plataforma.
  const healthProvider = platformHealthProvider();
  const groups = ["all", "attention", "messages", "calendar", "knowledge", "health", "entertainment"];
  const requestedGroup = searchParams.get("group") ?? "all";
  const group = groups.includes(requestedGroup) ? requestedGroup : "all";
  const enabledProviders = [
    ...expansionProviders,
    "strava",
    "google_calendar",
    ...(gmailEnabled ? ["gmail"] : []),
    ...(googleDriveEnabled ? ["google_drive"] : []),
    "outlook",
    ...(outlookEnabled ? ["outlook_mail"] : []),
    ...(oneDriveEnabled ? ["onedrive"] : []),
    "google_tasks",
    ...(googleContactsEnabled ? ["google_contacts"] : []),
    ...(microsoftContactsEnabled ? ["microsoft_contacts"] : []),
    ...(microsoftTodoEnabled ? ["microsoft_todo"] : []),
    ...(FEATURE_FLAGS.APPLE_CONTACTS_ENABLED && nativePeopleTasksAvailable() ? ["apple_contacts"] : []),
    ...(FEATURE_FLAGS.APPLE_REMINDERS_ENABLED && nativePeopleTasksAvailable() ? ["apple_reminders"] : []),
    "notion",
    ...(isIos ? ["apple_calendar"] : []),
    ...(healthProvider ? [healthProvider] : []),
    ...(FEATURE_FLAGS.SPOTIFY_ENABLED ? ["spotify"] : []),
  ];
  // Discovery is independent of activation. These providers already have an
  // implementation; live controls require the public flags or the validation cohort.
  const preparedProviders = [
    "gmail", "outlook_mail", "google_drive", "onedrive",
    "google_contacts", "microsoft_contacts", "microsoft_todo",
    ...EXPANSION_PROVIDERS,
    ...(nativePeopleTasksAvailable() ? ["apple_contacts", "apple_reminders"] : []),
  ];
  const pendingProviders = preparedProviders.filter(provider => !enabledProviders.includes(provider));
  const visibleProviders = [...enabledProviders, ...pendingProviders];
  const activeCount = connections.filter(connection => enabledProviders.includes(connection.provider) && connection.status === "active").length + (whatsappLink.connected ? 1 : 0);
  const attentionCount = connections.filter(connection => enabledProviders.includes(connection.provider) && connectionNeedsAttention(connection)).length;
  const showProvider = (provider: string) => group === "all" || connectionGroups[provider] === group || (group === "attention" && enabledProviders.includes(provider) && connectionNeedsAttention(connections.find(item => item.provider === provider)));
  const setGroup = (value: string) => { const next = new URLSearchParams(searchParams); next.set("group", value); setSearchParams(next, { replace: true }); };
  const catalogMeta: Record<string, { title: string; description: string; keywords?: string }> = {
    ...Object.fromEntries(EXPANSION_PROVIDERS.map(provider => [provider, {title:t("actions.provider."+provider),description:t(expansionBenefitKey(provider))}])),
    whatsapp: { title: t("whatsappLink.title"), description: t("whatsappLink.description") },
    strava: { title: t("strava.name"), description: t("strava.description") },
    google_calendar: { title: t("settings.googleCal.title"), description: t("settings.googleCal.subtitle") },
    gmail: { title: t("settings.gmail.title"), description: t("settings.gmail.subtitle"), keywords: "email e-mail Google" },
    google_drive: { title: t("settings.googleDrive.title"), description: t("settings.googleDrive.subtitle") },
    outlook: { title: t("settings.outlookCal.title"), description: t("settings.outlookCal.subtitle") },
    outlook_mail: { title: t("settings.outlookMail.title"), description: t("settings.outlookMail.subtitle"), keywords: "email e-mail Hotmail Microsoft" },
    onedrive: { title: t("settings.oneDrive.title"), description: t("settings.oneDrive.subtitle") },
    google_contacts: { title: t("people.provider.google_contacts"), description: t("people.contactsBenefit") },
    microsoft_contacts: { title: t("people.provider.microsoft_contacts"), description: t("people.contactsBenefit") },
    microsoft_todo: { title: t("people.provider.microsoft_todo"), description: t("people.tasksBenefit") },
    apple_contacts: { title: t("people.provider.apple_contacts"), description: t("people.contactsBenefit") },
    apple_reminders: { title: t("people.provider.apple_reminders"), description: t("people.tasksBenefit") },
    google_tasks: { title: t("settings.googleTasks.title"), description: t("settings.googleTasks.subtitle") },
    apple_calendar: { title: t("settings.appleCal.title"), description: t("settings.appleCal.subtitle") },
    notion: { title: t("settings.notion.title"), description: t("settings.notion.subtitle") },
    spotify: { title: t("settings.spotify.title"), description: t("settings.spotify.subtitle") },
    ...(healthProvider ? { [healthProvider]: { title: t(healthProvider + ".name"), description: t(healthProvider + ".description") } } : {}),
  };
  const normalizeSearch = (value: string) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase();
  const normalizedQuery = normalizeSearch(catalogQuery.trim());
  const matchesQuery = (provider: string) => {
    if (!normalizedQuery) return true;
    const meta = catalogMeta[provider];
    return !!meta && normalizeSearch(meta.title + " " + meta.description + " " + (meta.keywords ?? "")).includes(normalizedQuery);
  };
  const showCatalogProvider = (provider: string) => showProvider(provider) && matchesQuery(provider);
  const renderPreparedProvider = (provider: string) => showCatalogProvider(provider) && (
    <PreparedConnectionCard key={provider} provider={provider}
      title={catalogMeta[provider].title} description={catalogMeta[provider].description} />
  );
  const emailPendingProviders = pendingProviders.filter(provider => provider === "gmail" || provider === "outlook_mail");
  const otherPendingProviders = pendingProviders.filter(provider => provider !== "gmail" && provider !== "outlook_mail");
  const catalogProviderIds = ["whatsapp", ...visibleProviders];
  const visibleCatalogCount = catalogProviderIds.filter(showCatalogProvider).length;
  const connectedProviderIds = [
    ...(whatsappLink.connected ? ["whatsapp"] : []),
    ...connections.filter(connection => enabledProviders.includes(connection.provider) && connection.status === "active").map(connection => connection.provider),
  ].filter((provider, index, all) => all.indexOf(provider) === index);
  const scrollToProvider = (provider: string) => {
    const node = document.querySelector<HTMLElement>(`[data-provider-card="${provider}"]`);
    node?.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "center" });
  };


  const expansionOauthHandled = useRef(false);
  useEffect(() => {
    const result = searchParams.get("expansion");
    if (!result || expansionOauthHandled.current) return;
    expansionOauthHandled.current = true;
    if (result === "connected") { toast.success(t("people.connected")); void refresh(); }
    else toast.error(t("expansion.oauthError"));
    const next = new URLSearchParams(searchParams); next.delete("expansion"); next.delete("provider"); next.delete("reason");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, refresh, t]);
  const expansionConnect = async (provider: ExpansionProvider) => { await connectExpansion(provider, false, expansionOwner ?? undefined); await refresh(); };
  const expansionProbe = async (provider: ExpansionProvider) => { const {owner}=await expansionSession(expansionOwner ?? undefined); await expansionRequest(owner,{provider,action:"probe"}); await refresh(); };
  const expansionDisconnect = async (provider: ExpansionProvider) => { await disconnectExpansion(provider, expansionOwner ?? undefined); await refresh(); };

  const peopleOauthHandled = useRef(false);
  useEffect(() => {
    const result = searchParams.get("people_tasks");
    if (!result || peopleOauthHandled.current) return;
    peopleOauthHandled.current = true;
    if (result === "connected") { toast.success(t("people.connected")); void refresh(); }
    else toast.error(t("people.error.generic"));
    const next = new URLSearchParams(searchParams);
    next.delete("people_tasks"); next.delete("provider"); next.delete("reason");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, refresh, t]);
  const peopleConnect = async (provider: PeopleTaskProvider) => { await connectPeopleTasks(provider); await refresh(); };
  const peopleProbe = async (provider: PeopleTaskProvider) => { await invokePeopleTask({ provider, action: "probe" }); await refresh(); toast.success(t("people.connected")); };
  const peopleDisconnect = async (provider: PeopleTaskProvider) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("invalid_session");
    const { data, error } = await supabase.from("user_connections").delete().eq("user_id", user.id).eq("provider", provider).select("id");
    if (error || data?.length !== 1) throw error || new Error("disconnect_failed");
    await refresh();
  };
  const openEmail = (provider: EmailProvider) => setEmailPrefill({ provider, action: "search", query: "", to: [], cc: [], bcc: [], subject: "", body_text: "", action_id: crypto.randomUUID() });
  const openFiles = (provider: FileProvider) => { setFileOrigin(null); setFilePrefill({ provider, action: "search", query: "", name: "", content_text: "", action_id: crypto.randomUUID() }); };
  const dropboxConnection = connections.find(connection => connection.provider === "dropbox");
  const dropboxReaderReady = dropboxEnabled && !!fileOwner && expansionOwner === fileOwner && connectionOwner === fileOwner && dropboxConnection?.status === "active" && !loading && !connectionsError && !syncing && !connectingProvider && !syncProvider;
  fileReaderSource.current = [fileOwner, expansionOwner, connectionOwner, dropboxConnection?.id, dropboxConnection?.status, dropboxReaderReady].join(":");
  const openDropboxFiles = async () => {
    if (!dropboxReaderReady || fileReaderLock.current) return;
    const source = fileReaderSource.current;
    fileReaderLock.current = true; setFileReaderOpening(true);
    try {
      const session = await fileOwnerSession(fileOwner);
      await fileOwnerSession(fileOwner, session.sid);
      if (source !== fileReaderSource.current) return;
      setFileOrigin({ ownerId: fileOwner, authSessionId: session.sid });
      setFilePrefill({ provider: "dropbox", action: "search", query: "", name: "", content_text: "", action_id: crypto.randomUUID() });
    } catch { toast.error(t("mirror.files.read.accountChanged")); }
    finally { fileReaderLock.current = false; setFileReaderOpening(false); }
  };
  useEffect(() => {
    if (!fileOrigin) return;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user.id === fileOrigin.ownerId && connectedFileReadSessionId(session.access_token) === fileOrigin.authSessionId) return;
      setFilePrefill(null); setFileOrigin(null);
    });
    return () => subscription.unsubscribe();
  }, [fileOrigin]);
  const openPeople = (provider: PeopleTaskProvider) => {
    if (provider === "google_contacts" || provider === "microsoft_contacts") {
      if (peopleOwner) setContactPrefill({ provider, action: "search", query: "", action_id: crypto.randomUUID(), expected_user_id: peopleOwner });
      return;
    }
    if (provider === "google_tasks" || provider === "microsoft_todo") {
      if (peopleOwner) setExternalTaskPrefill({provider,action:"search",query:"",action_id:crypto.randomUUID(),expected_user_id:peopleOwner});
      return;
    }
    setPeoplePrefill({ provider, kind: provider.endsWith("contacts") ? "contact" : "task", action: "search", action_id: crypto.randomUUID(), expected_user_id: peopleOwner ?? undefined });
  };

  // Handle the post-OAuth Google redirect (?google_cal=connected | error).
  // Show the right toast once, then strip the query param so a refresh
  // doesn't replay the toast. refresh() pulls the updated connection state
  // so the ConnectionCard flips from "Connect" to "Sync now" immediately.
  const oauthResultHandledRef = useRef(false);
  useEffect(() => {
    if (oauthResultHandledRef.current) return;
    const result = searchParams.get("google_cal");
    if (!result) return;
    oauthResultHandledRef.current = true;
    if (result === "connected") {
      toast.success(t("settings.googleCal.connectSuccess"));
      void refresh();
    } else if (result === "error") {
      const reason = searchParams.get("reason");
      toast.error(t("settings.googleCal.connectFailed") + (reason ? " (" + reason + ")" : ""));
    }
    const next = new URLSearchParams(searchParams);
    next.delete("google_cal");
    next.delete("reason");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, refresh, t]);

  // Handle the post-OAuth Outlook redirect (?outlook=connected | error).
  // Same once-guard + param-strip pattern as the Google Calendar handler.
  const outlookOauthResultHandledRef = useRef(false);
  useEffect(() => {
    if (outlookOauthResultHandledRef.current) return;
    const result = searchParams.get("outlook");
    if (!result) return;
    outlookOauthResultHandledRef.current = true;
    if (result === "connected") {
      toast.success(t("settings.outlookCal.connectSuccess"));
      void refresh();
    } else if (result === "error") {
      const reason = searchParams.get("reason");
      toast.error(t("settings.outlookCal.connectFailed") + (reason ? " (" + reason + ")" : ""));
    }
    const next = new URLSearchParams(searchParams);
    next.delete("outlook");
    next.delete("reason");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, refresh, t]);

  // Email OAuth callbacks remain available while catalogue discovery is
  // separate from the flags that unlock connection and execution.
  const gmailOauthResultHandledRef = useRef(false);
  useEffect(() => {
    if (gmailOauthResultHandledRef.current) return;
    const result = searchParams.get("gmail");
    if (!result) return;
    gmailOauthResultHandledRef.current = true;
    if (result === "connected") { toast.success(t("settings.gmail.connectSuccess")); void refresh(); }
    else if (result === "error") { const reason = searchParams.get("reason"); toast.error(t("settings.gmail.connectFailed") + (reason ? " (" + reason + ")" : "")); }
    const next = new URLSearchParams(searchParams); next.delete("gmail"); next.delete("reason"); setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, refresh, t]);

  const outlookMailOauthResultHandledRef = useRef(false);
  useEffect(() => {
    if (outlookMailOauthResultHandledRef.current) return;
    const result = searchParams.get("outlook_mail");
    if (!result) return;
    outlookMailOauthResultHandledRef.current = true;
    if (result === "connected") { toast.success(t("settings.outlookMail.connectSuccess")); void refresh(); }
    else if (result === "error") { const reason = searchParams.get("reason"); toast.error(t("settings.outlookMail.connectFailed") + (reason ? " (" + reason + ")" : "")); }
    const next = new URLSearchParams(searchParams); next.delete("outlook_mail"); next.delete("reason"); setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, refresh, t]);

  const googleDriveOauthHandledRef = useRef(false);
  useEffect(() => {
    if (googleDriveOauthHandledRef.current) return;
    const result = searchParams.get("google_drive");
    if (!result) return;
    googleDriveOauthHandledRef.current = true;
    if (result === "connected") { toast.success(t("settings.googleDrive.connectSuccess")); void refresh(); }
    else if (result === "error") { const reason = searchParams.get("reason"); toast.error(t("settings.googleDrive.connectFailed") + (reason ? " (" + reason + ")" : "")); }
    const next = new URLSearchParams(searchParams); next.delete("google_drive"); next.delete("reason"); setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, refresh, t]);

  const oneDriveOauthHandledRef = useRef(false);
  useEffect(() => {
    if (oneDriveOauthHandledRef.current) return;
    const result = searchParams.get("onedrive");
    if (!result) return;
    oneDriveOauthHandledRef.current = true;
    if (result === "connected") { toast.success(t("settings.oneDrive.connectSuccess")); void refresh(); }
    else if (result === "error") { const reason = searchParams.get("reason"); toast.error(t("settings.oneDrive.connectFailed") + (reason ? " (" + reason + ")" : "")); }
    const next = new URLSearchParams(searchParams); next.delete("onedrive"); next.delete("reason"); setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, refresh, t]);

  // Handle the post-OAuth Google Tasks redirect (?google_tasks=connected | error).
  // Same once-guard + param-strip pattern as the Calendar handler above.
  const tasksOauthResultHandledRef = useRef(false);
  useEffect(() => {
    if (tasksOauthResultHandledRef.current) return;
    const result = searchParams.get("google_tasks");
    if (!result) return;
    tasksOauthResultHandledRef.current = true;
    if (result === "connected") {
      toast.success(t("settings.googleTasks.connectSuccess"));
      void refresh();
    } else if (result === "error") {
      const reason = searchParams.get("reason");
      toast.error(t("settings.googleTasks.connectFailed") + (reason ? " (" + reason + ")" : ""));
    }
    const next = new URLSearchParams(searchParams);
    next.delete("google_tasks");
    next.delete("reason");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, refresh, t]);

  // ── Strava handlers ─────────────────────────────────────────────────
  const handleStravaConnect = async () => {
    try {
      await connectStrava();
    } catch (err) {
      console.error("[SettingsConnections] strava connect failed:", err);
      toast.error(t("strava.callback.exchangeFailed"));
    }
  };

  const handleStravaSync = async () => {
    try {
      const result = await syncStrava();
      const synced = result?.synced ?? 0;
      toast.success(synced + " " + t("strava.connections.activitiesImported"));
    } catch (err) {
      console.error("[SettingsConnections] strava sync failed:", err);
      toast.error(t("strava.callback.unexpectedError"));
    }
  };

  const handleStravaDisconnect = async () => {
    try {
      await disconnectStrava();
      toast.success(t("strava.connections.disconnectButton"));
    } catch (err) {
      console.error("[SettingsConnections] strava disconnect failed:", err);
      toast.error(t("strava.callback.unexpectedError"));
      throw err;
    }
  };

  const handleHistorySync = async (provider: "strava" | "apple_health" | "health_connect", from?: string) => {
    if (syncLock.current) throw new Error("sync_in_progress");
    await runSync(provider, async () => {
      const options = { mode: "history" as const, ...(from ? { history_from: from.slice(0, 10) } : {}) };
      if (provider === "strava") await syncStrava(options);
      else await syncHealth(provider, options);
    });
  };

  // ── Google Calendar handlers ────────────────────────────────────────
  const handleGoogleCalConnect = async () => {
    try {
      await connectGoogleCal();
    } catch (err) {
      console.error("[SettingsConnections] google connect failed:", err);
      toast.error(t("settings.googleCal.connectFailed"));
    }
  };

  const handleGoogleCalSync = async () => {
    try {
      const result = await syncGoogleCal();
      const total = result?.synced ?? 0;
      toast.success(String(total) + " " + t("settings.googleCal.syncedEvents"));
    } catch (err) {
      console.error("[SettingsConnections] google sync failed:", err);
      toast.error(t("settings.googleCal.syncError"));
    }
  };

  const handleGoogleCalDisconnect = async () => {
    try {
      await disconnectGoogleCal();
      toast.success(t("settings.googleCal.disconnectSuccess"));
    } catch (err) {
      console.error("[SettingsConnections] google disconnect failed:", err);
      toast.error(t("settings.googleCal.disconnectFailed"));
      throw err;
    }
  };

  // ── Outlook Calendar handlers ───────────────────────────────────────
  const handleOutlookConnect = async () => {
    try {
      await connectOutlook();
    } catch (err) {
      console.error("[SettingsConnections] outlook connect failed:", err);
      toast.error(t("settings.outlookCal.connectFailed"));
    }
  };

  const handleOutlookSync = async () => {
    try {
      const result = await syncOutlook();
      const total = result?.synced ?? 0;
      toast.success(String(total) + " " + t("settings.outlookCal.syncedEvents"));
    } catch (err) {
      console.error("[SettingsConnections] outlook sync failed:", err);
      toast.error(t("settings.outlookCal.syncError"));
    }
  };

  const handleOutlookDisconnect = async () => {
    try {
      await disconnectOutlook();
      toast.success(t("settings.outlookCal.disconnectSuccess"));
    } catch (err) {
      console.error("[SettingsConnections] outlook disconnect failed:", err);
      toast.error(t("settings.outlookCal.disconnectFailed"));
      throw err;
    }
  };

  // ── Email Executor handlers ──────────────────────────────────────
  const handleGmailConnect = async () => { try { await connectGmail(); } catch (err) { console.error("[SettingsConnections] gmail connect failed:", err); toast.error(t("settings.gmail.connectFailed")); } };
  const handleGmailProbe = async () => { try { await syncGmail(); toast.success(t("settings.gmail.probeSuccess")); } catch (err) { console.error("[SettingsConnections] gmail probe failed:", err); toast.error(t("settings.gmail.syncError")); } };
  const handleGmailDisconnect = async () => { try { await disconnectGmail(); toast.success(t("settings.gmail.disconnectSuccess")); } catch (err) { console.error("[SettingsConnections] gmail disconnect failed:", err); toast.error(t("settings.gmail.disconnectFailed")); throw err; } };

  const handleOutlookMailConnect = async () => { try { await connectOutlookMail(); } catch (err) { console.error("[SettingsConnections] outlook mail connect failed:", err); toast.error(t("settings.outlookMail.connectFailed")); } };
  const handleOutlookMailProbe = async () => { try { await syncOutlookMail(); toast.success(t("settings.outlookMail.probeSuccess")); } catch (err) { console.error("[SettingsConnections] outlook mail probe failed:", err); toast.error(t("settings.outlookMail.syncError")); } };
  const handleOutlookMailDisconnect = async () => { try { await disconnectOutlookMail(); toast.success(t("settings.outlookMail.disconnectSuccess")); } catch (err) { console.error("[SettingsConnections] outlook mail disconnect failed:", err); toast.error(t("settings.outlookMail.disconnectFailed")); throw err; } };

  // ── Files & Knowledge Executor handlers ────────────────────────────
  const handleGoogleDriveConnect = async () => { try { await connectGoogleDrive(); } catch (err) { console.error("[SettingsConnections] Google Drive connect failed:", err); toast.error(t("settings.googleDrive.connectFailed")); } };
  const handleGoogleDriveProbe = async () => { try { await syncGoogleDrive(); toast.success(t("settings.googleDrive.probeSuccess")); } catch (err) { console.error("[SettingsConnections] Google Drive probe failed:", err); toast.error(t("settings.googleDrive.syncError")); } };
  const handleGoogleDriveDisconnect = async () => { try { await disconnectGoogleDrive(); toast.success(t("settings.googleDrive.disconnectSuccess")); } catch (err) { console.error("[SettingsConnections] Google Drive disconnect failed:", err); toast.error(t("settings.googleDrive.disconnectFailed")); throw err; } };

  const handleOneDriveConnect = async () => { try { await connectOneDrive(); } catch (err) { console.error("[SettingsConnections] OneDrive connect failed:", err); toast.error(t("settings.oneDrive.connectFailed")); } };
  const handleOneDriveProbe = async () => { try { await syncOneDrive(); toast.success(t("settings.oneDrive.probeSuccess")); } catch (err) { console.error("[SettingsConnections] OneDrive probe failed:", err); toast.error(t("settings.oneDrive.syncError")); } };
  const handleOneDriveDisconnect = async () => { try { await disconnectOneDrive(); toast.success(t("settings.oneDrive.disconnectSuccess")); } catch (err) { console.error("[SettingsConnections] OneDrive disconnect failed:", err); toast.error(t("settings.oneDrive.disconnectFailed")); throw err; } };

  // ── Google Tasks handlers ───────────────────────────────────────────
  const handleGoogleTasksConnect = async () => {
    try {
      await connectGoogleTasks();
    } catch (err) {
      console.error("[SettingsConnections] google tasks connect failed:", err);
      toast.error(t("settings.googleTasks.connectFailed"));
    }
  };

  const handleGoogleTasksSync = async () => {
    try {
      const result = await syncGoogleTasks();
      const total = result?.synced ?? 0;
      if (result.status === "partial" || result.errors.length) toast.warning(t("settings.googleTasks.syncPartial"));
      else toast.success(String(total) + " " + t("settings.googleTasks.syncedTasks"));
    } catch (err) {
      console.error("[SettingsConnections] google tasks sync failed:", err);
      toast.error(t("settings.googleTasks.syncError"));
    }
  };

  const handleGoogleTasksDisconnect = async () => {
    try {
      await disconnectGoogleTasks();
      toast.success(t("settings.googleTasks.disconnectSuccess"));
    } catch (err) {
      console.error("[SettingsConnections] google tasks disconnect failed:", err);
      toast.error(t("settings.googleTasks.disconnectFailed"));
      throw err;
    }
  };

  // ── Apple Calendar handlers (EventKit, on-device, iOS-only) ─────────
  const handleAppleCalConnect = async () => {
    try {
      await connectAppleCal();
      // Connect grants permission but doesn't pull yet — kick a first sync so
      // the card flips straight to a populated state.
      const result = await syncAppleCal();
      if (result.status === "partial" || result.errors.length) toast.warning(t("settings.appleCal.syncPartial"));
      else toast.success(t("settings.appleCal.connectSuccess"));
    } catch (err) {
      // Captured to Sentry inside the hook; surface the real message so the
      // on-device cause is visible where console logs aren't.
      console.error("[SettingsConnections] apple cal connect failed:", err);
      // `String(err)` num objeto da ponte vira "[object Object]" — o oposto do
      // que o comentário acima promete. O extrator lê o objeto simples.
      const detail = capacitorErrorMessage(err, "");
      toast.error(t("settings.appleCal.connectFailed") + (detail ? ` (${detail})` : ""));
    }
  };

  const handleAppleCalSync = async () => {
    try {
      const result = await syncAppleCal();
      const total = result?.synced ?? 0;
      const found = result?.total_fetched ?? 0;
      // When nothing imported, show how many EventKit actually returned —
      // "found 0" points at permission/window; found>0 with 0 synced means
      // everything was already up to date (etag skip).
      const suffix = total === 0 ? ` (${tf("settings.appleCal.foundEvents", { found: String(found) })})` : "";
      if (result.status === "partial" || result.errors.length) toast.warning(t("settings.appleCal.syncPartial"));
      else toast.success(String(total) + " " + t("settings.appleCal.syncedEvents") + suffix);
    } catch (err) {
      console.error("[SettingsConnections] apple cal sync failed:", err);
      const detail = capacitorErrorMessage(err, "");
      toast.error(t("settings.appleCal.syncError") + (detail ? ` (${detail})` : ""));
    }
  };

  const handleAppleCalDisconnect = async () => {
    try {
      await disconnectAppleCal();
      toast.success(t("settings.appleCal.disconnectSuccess"));
    } catch (err) {
      console.error("[SettingsConnections] apple cal disconnect failed:", err);
      toast.error(t("settings.appleCal.disconnectFailed"));
      throw err;
    }
  };

  // ── Render ──────────────────────────────────────────────────────────
  const calendarProductivity = integrationsByCategory("calendar_productivity");
  const wearablesHealth = integrationsByCategory("wearables_health");


  // Post-OAuth redirects para Notion e Spotify. Mesmo once-guard + limpeza de
  // parâmetro do handler do Google Tasks logo acima.
  const notionOauthHandledRef = useRef(false);
  useEffect(() => {
    if (notionOauthHandledRef.current) return;
    const result = searchParams.get("notion");
    if (!result) return;
    notionOauthHandledRef.current = true;
    if (result === "connected") {
      toast.success(t("settings.notion.connectSuccess"));
      void refresh();
    } else if (result === "error") {
      const reason = searchParams.get("reason");
      toast.error(t("settings.notion.connectFailed") + (reason ? " (" + reason + ")" : ""));
    }
    const next = new URLSearchParams(searchParams);
    next.delete("notion");
    next.delete("reason");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, refresh, t]);

  const spotifyOauthHandledRef = useRef(false);
  useEffect(() => {
    if (spotifyOauthHandledRef.current) return;
    const result = searchParams.get("spotify");
    if (!result) return;
    spotifyOauthHandledRef.current = true;
    if (result === "connected") {
      toast.success(t("settings.spotify.connectSuccess"));
      void refresh();
    } else if (result === "error") {
      const reason = searchParams.get("reason");
      toast.error(t("settings.spotify.connectFailed") + (reason ? " (" + reason + ")" : ""));
    }
    const next = new URLSearchParams(searchParams);
    next.delete("spotify");
    next.delete("reason");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, refresh, t]);


  // ── Health (Apple Saúde / Health Connect) handlers ──────────────────
  // Plugin AUSENTE do binário (Capacitor "not implemented", Sentry
  // VYV-FRONTEND-4W) não é falha de permissão: mandar o usuário para
  // Ajustes → Privacidade quando o componente nem existe na instalação
  // mente sobre a causa e custou uma investigação. A mensagem certa é
  // "atualize o app" — pluginMissing, nos 6 locales.
  const handleHealthConnect = async () => {
    if (!healthProvider) return;
    try {
      await connectHealth(healthProvider);
      toast.success(t(healthProvider + ".connections.connectSuccess"));
    } catch (err) {
      console.error("[SettingsConnections] health connect failed:", err);
      toast.error(t(healthProvider + ".connections." +
        (isPluginUnimplemented(err) ? "pluginMissing" : "connectFailed")));
    }
  };

  const handleHealthSync = async () => {
    if (!healthProvider) return;
    try {
      const result = await syncHealth(healthProvider);
      toast.success(String(result?.synced ?? 0) + " " + t(healthProvider + ".connections.activitiesImported"));
    } catch (err) {
      console.error("[SettingsConnections] health sync failed:", err);
      toast.error(t(healthProvider + ".connections." +
        (isPluginUnimplemented(err) ? "pluginMissing" : "syncFailed")));
    }
  };

  // Os tipos que o aparelho NÃO libera, como o último sync registrou. O card
  // não chama o plugin — lê o que o banco já sabe (issue #480).
  const healthMissing = deniedFromMetadata(healthConnection?.provider_metadata);

  const handleHealthFixPermissions = async () => {
    if (!healthProvider) return;
    try {
      await fixHealthPermissions(healthProvider);
    } catch (err) {
      console.error("[SettingsConnections] health permission fix failed:", err);
      toast.error(t("connections.permissionFixFailed") + ` (${capacitorErrorMessage(err, "")})`);
    }
  };

  const handleHealthDisconnect = async () => {
    if (!healthProvider) return;
    try {
      await disconnectHealth(healthProvider);
      toast.success(t(healthProvider + ".connections.disconnectSuccess"));
    } catch (err) {
      console.error("[SettingsConnections] health disconnect failed:", err);
      toast.error(t(healthProvider + ".connections.disconnectFailed"));
      throw err;
    }
  };

  // ── Notion handlers ─────────────────────────────────────────────────
  const handleNotionConnect = async () => {
    try {
      await connectNotion();
    } catch (err) {
      console.error("[SettingsConnections] notion connect failed:", err);
      toast.error(t("settings.notion.connectFailed"));
    }
  };

  const handleNotionSync = async () => {
    try {
      const result = await syncNotion();
      toast.success(String(result?.synced ?? 0) + " " + t("settings.notion.syncedNotes"));
    } catch (err) {
      console.error("[SettingsConnections] notion sync failed:", err);
      toast.error(t("settings.notion.syncError"));
    }
  };

  const handleNotionDisconnect = async () => {
    try {
      await disconnectNotion();
      toast.success(t("settings.notion.disconnectSuccess"));
    } catch (err) {
      console.error("[SettingsConnections] notion disconnect failed:", err);
      toast.error(t("settings.notion.disconnectFailed"));
      throw err;
    }
  };

  // ── Spotify handlers ────────────────────────────────────────────────
  const handleSpotifyConnect = async () => {
    try {
      await connectSpotify();
    } catch (err) {
      console.error("[SettingsConnections] spotify connect failed:", err);
      toast.error(t("settings.spotify.connectFailed"));
    }
  };

  const handleSpotifySync = async () => {
    try {
      const result = await syncSpotify();
      toast.success(String(result?.synced ?? 0) + " " + t("settings.spotify.syncedDays"));
    } catch (err) {
      console.error("[SettingsConnections] spotify sync failed:", err);
      toast.error(t("settings.spotify.syncError"));
    }
  };

  const handleSpotifyDisconnect = async () => {
    try {
      await disconnectSpotify();
      toast.success(t("settings.spotify.disconnectSuccess"));
    } catch (err) {
      console.error("[SettingsConnections] spotify disconnect failed:", err);
      toast.error(t("settings.spotify.disconnectFailed"));
      throw err;
    }
  };

  return (
    <MotionConfig reducedMotion="user"><div className="min-h-screen bg-background">
      <div
        className="max-w-5xl mx-auto px-5 pb-24"
        style={{ paddingTop: "max(env(safe-area-inset-top, 0px) + 1rem, 2rem)" }}
      >
        <button
          onClick={() => navigate("/mirror")}
          className="min-h-11 flex items-center text-muted-foreground hover:text-foreground mb-6 transition-colors focus-visible:ring-2 focus-visible:ring-primary"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          {t("common.back")}
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-2 mb-8"
        >
          <h1 className="text-3xl font-display font-medium">
            {t("strava.connections.title")}
          </h1>
          <p className="text-muted-foreground">
            {t("connections.ux.subtitle")}
          </p>
        </motion.div>

        {import.meta.env.VITE_CREATOR_CONNECTIONS_ENABLED === "true" && <Suspense fallback={null}>
          <CreatorConnections owner={expansionOwner} />
        </Suspense>}
        <section className="mb-7 space-y-4">
          {!loading && !connectionsError && connectionOwner && ((filesEnabled && googleDriveEnabled && googleDriveConnection?.status === "active") || (FEATURE_FLAGS.FILES_EXECUTOR_ENABLED && notionConnection?.status === "active") || (dropboxEnabled && connections.some(c => c.provider === "dropbox" && c.status === "active"))) && <button type="button" className="min-h-11 rounded-xl border border-border px-4 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary" onClick={() => setFileEditOpen(true)}>{t("fileEdit.open")}</button>}
          {FEATURE_FLAGS.PERSONAL_ACTION_ENGINE_ENABLED && <button type="button" className="min-h-11 rounded-xl border border-border px-4 py-2 text-sm" onClick={() => navigate("/actions")}>{t("actions.title")}</button>}
          {!loading && !connectionsError && (
            <p className="text-sm text-muted-foreground" role="status">
              {tf("connections.ux.overview", { active: String(activeCount), attention: String(attentionCount) })}
            </p>
          )}

          {!connectionsError && connectedProviderIds.length > 0 && (
            <div className="rounded-2xl border border-border/80 bg-card/70 p-3 sm:p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                {t("connections.ux.connectedApps")}
              </p>
              <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {connectedProviderIds.map((provider) => {
                  const meta = catalogMeta[provider];
                  if (!meta) return null;
                  return (
                    <button
                      type="button"
                      key={provider}
                      onClick={() => scrollToProvider(provider)}
                      className="shrink-0 rounded-xl border border-border/80 bg-background px-2.5 py-2 transition-colors hover:border-primary/40"
                    >
                      <ConnectionBrandInline provider={provider} label={meta.title} />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <label className="relative block">
            <span className="sr-only">{t("connections.ux.search")}</span>
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={catalogQuery}
              onChange={(event) => setCatalogQuery(event.target.value)}
              placeholder={t("connections.ux.search")}
              className="min-h-12 w-full rounded-2xl border border-border bg-card pl-10 pr-4 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
            />
          </label>

          <nav className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label={t("connections.ux.groups")}>
            {groups.map((item) => (
              <button
                type="button"
                key={item}
                onClick={() => setGroup(item)}
                aria-pressed={group === item}
                className={`min-h-11 shrink-0 rounded-full border px-4 py-2 text-sm font-medium focus-visible:ring-2 focus-visible:ring-primary ${group === item ? "border-primary/40 bg-primary/15 text-primary" : "border-border bg-card text-muted-foreground"}`}
              >
                {t("connections.ux.group." + item)}
              </button>
            ))}
          </nav>

          <button
            type="button"
            onClick={() => void refresh()}
            disabled={loading}
            className="min-h-11 inline-flex items-center gap-2 text-sm font-medium text-primary disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? "animate-spin motion-reduce:animate-none" : undefined} />
            {t("connections.ux.refresh")}
          </button>
        </section>
        {/* "não consegui LER as conexões" ≠ "você não tem conexões".
            Até 03/08 uma falha de leitura (sessão expirada é a comum) fazia a
            lista virar [] e TODO card conectado se apresentar como
            desconectado, oferecendo "Conectar" para quem já estava conectado.
            Foi isso em 01/08: o usuário tinha apple_health `active` no banco,
            viu o card desconectado, tocou em Conectar e levou "Not
            authenticated". A lista agora é preservada, e esta faixa é o que
            impede o usuário de agir sobre um estado que o app não leu. */}
        {connectionsError && (
          <div role="alert" className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-500/25 bg-amber-500/5 px-4 py-3">
            <p className="max-w-xl text-sm text-foreground">{t("connections.readFailed")}</p>
            <button type="button" onClick={() => void refresh()} disabled={loading} className="min-h-11 px-3 text-sm text-primary">{t("common.retry")}</button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* ── Section 1 — Active integrations ─────────────────── */}
            <section className="space-y-3">
              {group === "attention" && !connectionsError && attentionCount === 0 && <p role="status" className="rounded-xl border border-border bg-card p-5 text-sm text-muted-foreground">{t("connections.ux.noAttention")}</p>}
              {visibleCatalogCount === 0 && !(group === "health" && !healthProvider) && (
                <p role="status" className="rounded-2xl border border-dashed border-border bg-card/50 p-6 text-center text-sm text-muted-foreground">
                  {t("connections.ux.noMatches")}
                </p>
              )}
              <div className="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-2">
              {showCatalogProvider("whatsapp") && <WhatsAppLinkCardView link={whatsappLink} />}
              {emailPendingProviders.map(renderPreparedProvider)}
              <fieldset disabled={!!connectionsError} className="contents">
              {dropboxEnabled && showCatalogProvider("dropbox") && <div className="min-w-0 space-y-2"><ConnectionCard
                stateUnavailable={!!connectionsError}
                provider="dropbox" title={t("actions.provider.dropbox")} description={t(expansionBenefitKey("dropbox"))}
                connection={connections.find(c => c.provider === "dropbox") ?? null} busy={loading || !!connectionsError}
                onConnect={() => expansionConnect("dropbox")} onSync={() => expansionProbe("dropbox")} onDisconnect={() => expansionDisconnect("dropbox")}
                onViewData={() => navigate("/connections/dropbox")} viewDataLabel={t("expansion.open")}
              />
                {dropboxConnection?.status === "active" && <button type="button" disabled={!dropboxReaderReady || fileReaderOpening} onClick={() => void openDropboxFiles()} className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-border px-4 text-sm font-semibold disabled:opacity-50"><FileText className="h-4 w-4" />{t("mirror.files.search.title")}</button>}
              </div>}

              {todoistEnabled && showCatalogProvider("todoist") && <ConnectionCard
                stateUnavailable={!!connectionsError}
                provider="todoist" title={t("actions.provider.todoist")} description={t(expansionBenefitKey("todoist"))}
                connection={connections.find(c => c.provider === "todoist") ?? null} busy={loading || !!connectionsError}
                onConnect={() => expansionConnect("todoist")} onSync={() => expansionProbe("todoist")} onDisconnect={() => expansionDisconnect("todoist")}
                onViewData={() => navigate("/connections/todoist")} viewDataLabel={t("expansion.open")}
              />}

              {trelloEnabled && showCatalogProvider("trello") && <ConnectionCard
                stateUnavailable={!!connectionsError}
                provider="trello" title={t("actions.provider.trello")} description={t(expansionBenefitKey("trello"))}
                connection={connections.find(c => c.provider === "trello") ?? null} busy={loading || !!connectionsError}
                onConnect={() => expansionConnect("trello")} onSync={() => expansionProbe("trello")} onDisconnect={() => expansionDisconnect("trello")}
                onViewData={() => navigate("/connections/trello")} viewDataLabel={t("expansion.open")}
              />}

              {slackEnabled && showCatalogProvider("slack") && <ConnectionCard
                stateUnavailable={!!connectionsError}
                provider="slack" title={t("actions.provider.slack")} description={t(expansionBenefitKey("slack"))}
                connection={connections.find(c => c.provider === "slack") ?? null} busy={loading || !!connectionsError}
                onConnect={() => expansionConnect("slack")} onSync={() => expansionProbe("slack")} onDisconnect={() => expansionDisconnect("slack")}
                onViewData={() => navigate("/connections/slack")} viewDataLabel={t("expansion.open")}
              />}

              {microsoftTeamsEnabled && showCatalogProvider("microsoft_teams") && <ConnectionCard
                stateUnavailable={!!connectionsError}
                provider="microsoft_teams" title={t("actions.provider.microsoft_teams")} description={t(expansionBenefitKey("microsoft_teams"))}
                connection={connections.find(c => c.provider === "microsoft_teams") ?? null} busy={loading || !!connectionsError}
                onConnect={() => expansionConnect("microsoft_teams")} onSync={() => expansionProbe("microsoft_teams")} onDisconnect={() => expansionDisconnect("microsoft_teams")}
                onViewData={() => navigate("/connections/microsoft_teams")} viewDataLabel={t("expansion.open")}
              />}
              {showCatalogProvider("strava") && (<div className="space-y-3"><ConnectionCard
                stateUnavailable={!!connectionsError}
                provider="strava"
                icon={Activity}
                title={t("strava.name")}
                description={t("strava.description")}
                summary={t("connections.benefit.strava")}
                connection={stravaConnection}
                activityCount={stravaActivityCount}
                onViewData={() => navigate("/fitness")}
                viewDataLabel={t("connections.ux.open.fitness")}
                onConnect={handleStravaConnect}
                onSync={() => runSync("strava", handleStravaSync)}
                onDisconnect={handleStravaDisconnect}
                syncing={syncing && syncProvider === "strava"}
                busy={syncing || connectingProvider !== null}
                connecting={connectingProvider === "strava"}
              />
              {stravaConnection?.status === "active" && <ConnectionHistoryPanel
                key={`${connectionOwner}:${stravaConnection.id}:${stravaConnection.provider_user_id}`}
                provider="strava" history={stravaHistoryForAthlete(stravaConnection)}
                onImport={(from) => handleHistorySync("strava", from)} onContinue={() => handleHistorySync("strava")}
                busy={syncing || syncProvider !== null} disabled={loading || !!connectionsError || connectingProvider !== null}
              />}</div>)}
              {showCatalogProvider("google_calendar") && (<ConnectionCard
                stateUnavailable={!!connectionsError}
                provider="google_calendar"
                i18nPrefix="settings.googleCal"
                icon={Calendar}
                iconColorClass="text-blue-400"
                iconBgClass="bg-blue-500/10"
                connectBgClass="bg-blue-500"
                title={t("settings.googleCal.title")}
                description={t("settings.googleCal.subtitle")}
                summary={t("connections.benefit.google_calendar")}
                connection={googleCalConnection}
                                onViewData={() => navigate("/agenda")}
                viewDataLabel={t("connections.ux.open.calendar")}
                onConnect={handleGoogleCalConnect}
                onSync={() => runSync("google_calendar", handleGoogleCalSync)}
                onDisconnect={handleGoogleCalDisconnect}
                syncing={syncing && syncProvider === "google_calendar"}
                busy={syncing || connectingProvider !== null}
                connecting={connectingProvider === "google_calendar"}
              />)}
              {gmailEnabled && showCatalogProvider("gmail") && (<ConnectionCard
                stateUnavailable={!!connectionsError}
                provider="gmail"
                i18nPrefix="settings.gmail"
                title={t("settings.gmail.title")}
                description={t("settings.gmail.subtitle")}
                connection={gmailConnection}
                onViewData={() => openEmail("gmail")} viewDataLabel={t("mirror.email.search.title")}
                onConnect={handleGmailConnect}
                onSync={() => runSync("gmail", handleGmailProbe)}
                onDisconnect={handleGmailDisconnect}
                syncing={syncing && syncProvider === "gmail"}
                busy={syncing || connectingProvider !== null}
                connecting={connectingProvider === "gmail"}
              />)}
              {outlookEnabled && showCatalogProvider("outlook_mail") && (<ConnectionCard
                stateUnavailable={!!connectionsError}
                provider="outlook_mail"
                i18nPrefix="settings.outlookMail"
                title={t("settings.outlookMail.title")}
                description={t("settings.outlookMail.subtitle")}
                connection={outlookMailConnection}
                onViewData={() => openEmail("outlook_mail")} viewDataLabel={t("mirror.email.search.title")}
                onConnect={handleOutlookMailConnect}
                onSync={() => runSync("outlook_mail", handleOutlookMailProbe)}
                onDisconnect={handleOutlookMailDisconnect}
                syncing={syncing && syncProvider === "outlook_mail"}
                busy={syncing || connectingProvider !== null}
                connecting={connectingProvider === "outlook_mail"}
              />)}
              {googleDriveEnabled && showCatalogProvider("google_drive") && (<ConnectionCard
                stateUnavailable={!!connectionsError}
                provider="google_drive"
                i18nPrefix="settings.googleDrive"
                title={t("settings.googleDrive.title")}
                description={t("settings.googleDrive.subtitle")}
                connection={googleDriveConnection}
                onViewData={filesEnabled ? () => openFiles("google_drive") : undefined} viewDataLabel={t("mirror.files.search.title")}
                onConnect={handleGoogleDriveConnect}
                onSync={() => runSync("google_drive", handleGoogleDriveProbe)}
                onDisconnect={handleGoogleDriveDisconnect}
                syncing={syncing && syncProvider === "google_drive"}
                busy={syncing || connectingProvider !== null}
                connecting={connectingProvider === "google_drive"}
              />)}
              {oneDriveEnabled && showCatalogProvider("onedrive") && (<ConnectionCard
                stateUnavailable={!!connectionsError}
                provider="onedrive"
                i18nPrefix="settings.oneDrive"
                title={t("settings.oneDrive.title")}
                description={t("settings.oneDrive.subtitle")}
                connection={oneDriveConnection}
                onViewData={filesEnabled ? () => openFiles("onedrive") : undefined} viewDataLabel={t("mirror.files.search.title")}
                onConnect={handleOneDriveConnect}
                onSync={() => runSync("onedrive", handleOneDriveProbe)}
                onDisconnect={handleOneDriveDisconnect}
                syncing={syncing && syncProvider === "onedrive"}
                busy={syncing || connectingProvider !== null}
                connecting={connectingProvider === "onedrive"}
              />)}
              {showCatalogProvider("outlook") && (<ConnectionCard
                stateUnavailable={!!connectionsError}
                provider="outlook"
                i18nPrefix="settings.outlookCal"
                icon={CalendarClock}
                iconColorClass="text-sky-400"
                iconBgClass="bg-sky-500/10"
                connectBgClass="bg-sky-500"
                title={t("settings.outlookCal.title")}
                description={t("settings.outlookCal.subtitle")}
                summary={t("connections.benefit.outlook")}
                connection={outlookConnection}
                                onViewData={() => navigate("/agenda")}
                viewDataLabel={t("connections.ux.open.calendar")}
                onConnect={handleOutlookConnect}
                onSync={() => runSync("outlook", handleOutlookSync)}
                onDisconnect={handleOutlookDisconnect}
                syncing={syncing && syncProvider === "outlook"}
                busy={syncing || connectingProvider !== null}
                connecting={connectingProvider === "outlook"}
              />)}
              {googleContactsEnabled && showCatalogProvider("google_contacts") && <ConnectionCard
                stateUnavailable={!!connectionsError}
                provider="google_contacts" title={t("people.provider.google_contacts")} description={t("people.contactsBenefit")}
                i18nPrefix="people.connection" connection={connections.find(c => c.provider === "google_contacts") || null}
                onConnect={() => peopleConnect("google_contacts")} onSync={() => runSync("google_contacts", () => peopleProbe("google_contacts"))}
                onDisconnect={() => peopleDisconnect("google_contacts")} syncing={syncProvider === "google_contacts"}
                onViewData={() => openPeople("google_contacts")} viewDataLabel={t("people.open")}
              />}
              {microsoftContactsEnabled && showCatalogProvider("microsoft_contacts") && <ConnectionCard
                stateUnavailable={!!connectionsError}
                provider="microsoft_contacts" title={t("people.provider.microsoft_contacts")} description={t("people.contactsBenefit")}
                i18nPrefix="people.connection" connection={connections.find(c => c.provider === "microsoft_contacts") || null}
                onConnect={() => peopleConnect("microsoft_contacts")} onSync={() => runSync("microsoft_contacts", () => peopleProbe("microsoft_contacts"))}
                onDisconnect={() => peopleDisconnect("microsoft_contacts")} syncing={syncProvider === "microsoft_contacts"}
                onViewData={() => openPeople("microsoft_contacts")} viewDataLabel={t("people.open")}
              />}
              {microsoftTodoEnabled && showCatalogProvider("microsoft_todo") && <div className="space-y-2"><ConnectionCard
                stateUnavailable={!!connectionsError}
                provider="microsoft_todo" title={t("people.provider.microsoft_todo")} description={t("people.tasksBenefit")}
                i18nPrefix="people.connection" connection={connections.find(c => c.provider === "microsoft_todo") || null}
                onConnect={() => peopleConnect("microsoft_todo")} onSync={() => runSync("microsoft_todo", () => peopleProbe("microsoft_todo"))}
                onDisconnect={() => peopleDisconnect("microsoft_todo")} syncing={syncProvider === "microsoft_todo"}
                onViewData={() => openPeople("microsoft_todo")} viewDataLabel={t("people.open")}
              />
                <MicrosoftTodoImportLauncher ownerId={peopleOwner}
                  enabled={!connectionsError && connectionOwner === peopleOwner && connections.some(c => c.provider === "microsoft_todo" && c.status === "active")}
                  onImported={() => { void refresh(); }} />
              </div>}
              {FEATURE_FLAGS.APPLE_CONTACTS_ENABLED && nativePeopleTasksAvailable() && showCatalogProvider("apple_contacts") && <ConnectionCard
                stateUnavailable={!!connectionsError}
                provider="apple_contacts" title={t("people.provider.apple_contacts")} description={t("people.contactsBenefit")}
                i18nPrefix="people.connection" connection={connections.find(c => c.provider === "apple_contacts") || null}
                onConnect={() => peopleConnect("apple_contacts")} onSync={() => runSync("apple_contacts", () => peopleProbe("apple_contacts"))}
                onDisconnect={() => peopleDisconnect("apple_contacts")} syncing={syncProvider === "apple_contacts"}
                onViewData={() => openPeople("apple_contacts")} viewDataLabel={t("people.open")}
              />}
              {FEATURE_FLAGS.APPLE_REMINDERS_ENABLED && nativePeopleTasksAvailable() && showCatalogProvider("apple_reminders") && <ConnectionCard
                stateUnavailable={!!connectionsError}
                provider="apple_reminders" title={t("people.provider.apple_reminders")} description={t("people.tasksBenefit")}
                i18nPrefix="people.connection" connection={connections.find(c => c.provider === "apple_reminders") || null}
                onConnect={() => peopleConnect("apple_reminders")} onSync={() => runSync("apple_reminders", () => peopleProbe("apple_reminders"))}
                onDisconnect={() => peopleDisconnect("apple_reminders")} syncing={syncProvider === "apple_reminders"}
                onViewData={() => openPeople("apple_reminders")} viewDataLabel={t("people.open")}
              />}
              {showCatalogProvider("google_tasks") && (<ConnectionCard
                stateUnavailable={!!connectionsError}
                onViewData={googleTasksEnabled ? () => openPeople("google_tasks") : undefined}
                viewDataLabel={t("people.open")}
                provider="google_tasks"
                i18nPrefix="settings.googleTasks"
                icon={ListTodo}
                iconColorClass="text-amber-400"
                iconBgClass="bg-amber-500/10"
                connectBgClass="bg-amber-500"
                title={t("settings.googleTasks.title")}
                description={t("settings.googleTasks.subtitle")}
                summary={t("connections.benefit.google_tasks")}
                connection={googleTasksConnection}
                activityCount={googleTasksCount}
                onConnect={handleGoogleTasksConnect}
                onSync={() => runSync("google_tasks", handleGoogleTasksSync)}
                onDisconnect={handleGoogleTasksDisconnect}
                syncing={syncing && syncProvider === "google_tasks"}
                busy={syncing || connectingProvider !== null}
                connecting={connectingProvider === "google_tasks"}
              />)}
              {isIos && showCatalogProvider("apple_calendar") && (<ConnectionCard
                stateUnavailable={!!connectionsError}
                  provider="apple_calendar"
                  i18nPrefix="settings.appleCal"
                  icon={CalendarDays}
                  iconColorClass="text-gray-200"
                  iconBgClass="bg-gray-500/10"
                  connectBgClass="bg-gray-500"
                  title={t("settings.appleCal.title")}
                  description={t("settings.appleCal.subtitle")}
                summary={t("connections.benefit.apple_calendar")}
                  connection={appleCalConnection}
                                    onViewData={() => navigate("/agenda")}
                viewDataLabel={t("connections.ux.open.calendar")}
                onConnect={handleAppleCalConnect}
                  onSync={() => runSync("apple_calendar", handleAppleCalSync)}
                  onDisconnect={handleAppleCalDisconnect}
                  syncing={syncing && syncProvider === "apple_calendar"}
                busy={syncing || connectingProvider !== null}
                  connecting={connectingProvider === "apple_calendar"}
                />)}
              {/* Saúde nativa — UM card, dois provedores. É o hub do SO que
                  já recebe Apple Watch, Oura, Fitbit, WHOOP e Garmin, então o
                  VYV lê de um lugar só em vez de integrar com cada wearable.
                  Só aparece em iOS/Android: no navegador não há de onde ler. */}
              {healthProvider && showCatalogProvider(healthProvider) && (<div className="space-y-3"><ConnectionCard
                stateUnavailable={!!connectionsError}
                  provider={healthProvider}
                  i18nPrefix={healthProvider}
                  icon={HeartPulse}
                  iconColorClass="text-rose-400"
                  iconBgClass="bg-rose-500/10"
                  connectBgClass="bg-rose-500"
                  title={t(healthProvider + ".name")}
                  description={t(healthProvider + ".description")}
                  connection={healthConnection}
                  activityCount={healthItemCount}
                  onConnect={handleHealthConnect}
                  onSync={() => runSync(healthProvider, handleHealthSync)}
                  onDisconnect={handleHealthDisconnect}
                  syncing={syncing && syncProvider === healthProvider}
                busy={syncing || connectingProvider !== null}
                  connecting={connectingProvider === healthProvider}
                  onViewData={() => navigate("/health/metrics")}
                  viewDataLabel={t("health.viewMetrics")}
                  missingPermissions={healthMissing.map((tipo) => t(HEALTH_TYPE_LABEL_KEY[tipo]))}
                  onFixPermissions={handleHealthFixPermissions}
                  fixPermissionsLabel={t("connections.permissionFix")}
                  permissionHint={
                    Capacitor.getPlatform() === "ios" ? t("connections.permissionHintIos") : undefined
                  }
                />
                {healthConnection?.status === "active" && <ConnectionHistoryPanel
                  key={`${connectionOwner}:${healthConnection.id}`}
                  provider={healthProvider} history={healthConnection.provider_metadata?.history_sync}
                  onImport={(from) => handleHistorySync(healthProvider, from)} onContinue={() => handleHistorySync(healthProvider)}
                  busy={syncing || syncProvider !== null} disabled={loading || !!connectionsError || connectingProvider !== null}
                />}</div>)}
              {showCatalogProvider("notion") && (<ConnectionCard
                stateUnavailable={!!connectionsError}
                provider="notion"
                i18nPrefix="settings.notion"
                icon={FileText}
                iconColorClass="text-gray-200"
                iconBgClass="bg-gray-500/10"
                // Era bg-gray-500 — cinza é o affordance universal de
                // DESABILITADO, e o botão está 100% ativo; usuário nem
                // tentava (auditoria 30/07). Preto com borda é o CTA do
                // próprio Notion: identidade mantida, ação legível.
                connectBgClass="bg-black border border-white/25"
                title={t("settings.notion.title")}
                description={t("settings.notion.subtitle")}
                summary={t("connections.benefit.notion")}
                connection={notionConnection}
                activityCount={notionItemCount}
                onViewData={FEATURE_FLAGS.FILES_EXECUTOR_ENABLED ? () => openFiles("notion") : () => navigate("/notes")}
                viewDataLabel={t(FEATURE_FLAGS.FILES_EXECUTOR_ENABLED ? "mirror.files.search.title" : "connections.ux.open.notes")}
                onConnect={handleNotionConnect}
                onSync={() => runSync("notion", handleNotionSync)}
                onDisconnect={handleNotionDisconnect}
                syncing={syncing && syncProvider === "notion"}
                busy={syncing || connectingProvider !== null}
                connecting={connectingProvider === "notion"}
              />)}
              {/* Atrás de flag por política de plataforma, não por bug: o app
                  do Spotify está em Development Mode (5 usuários allowlisted;
                  o resto recebe 403), e o Extended Quota exige 250k MAU. As
                  functions seguem deployadas e corretas — reativar é
                  VITE_SPOTIFY_ENABLED=true. Ver featureFlags.ts. */}
              {FEATURE_FLAGS.SPOTIFY_ENABLED && showCatalogProvider("spotify") && (<ConnectionCard
                stateUnavailable={!!connectionsError}
                  provider="spotify"
                  i18nPrefix="settings.spotify"
                  icon={Music}
                  iconColorClass="text-green-400"
                  iconBgClass="bg-green-500/10"
                  connectBgClass="bg-green-500"
                  title={t("settings.spotify.title")}
                  description={t("settings.spotify.subtitle")}
                summary={t("connections.benefit.spotify")}
                  connection={spotifyConnection}
                  activityCount={spotifyItemCount}
                  onConnect={handleSpotifyConnect}
                  onSync={() => runSync("spotify", handleSpotifySync)}
                  onDisconnect={handleSpotifyDisconnect}
                  syncing={syncing && syncProvider === "spotify"}
                busy={syncing || connectingProvider !== null}
                  connecting={connectingProvider === "spotify"}
                />)}
              {/* Oura + Fitbit cards removed: OAuth scaffolding existed but was
                  never operationalized, so the live app no longer shows
                  non-working connectors. Native health (Apple Health / Health
                  Connect) is a single coming-soon tile in the Wearables &
                  health section; the real read lands in PR #176. */}
              </fieldset>
              {otherPendingProviders.map(renderPreparedProvider)}
              {(group === "health" || group === "all") && !healthProvider && <div className="rounded-2xl border border-border bg-card p-4 md:col-span-2"><h2 className="text-base font-medium">{t("health.title")}</h2><p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t("health.nativeOnly")}</p><button type="button" onClick={() => navigate("/health/metrics")} className="mt-2 min-h-11 inline-flex items-center gap-2 text-sm text-primary">{t("health.viewMetrics")}<ArrowUpRight size={16} /></button></div>}
              </div>
            </section>

            {/* ── Section 2 — Calendar & productivity ─────────────── */}
            {/* Seção só existe se sobrou algum "em breve". Notion e Spotify
                graduaram para cards ativos em 29/07 e a lista ficou vazia —
                sem esta guarda, sobraria um cabeçalho solto sem nada embaixo. */}
            {calendarProductivity.length > 0 && (
              <section className="space-y-3">
                <SectionHeader label={t(SECTIONS[1].headerKey)} />
                <div className="space-y-2">
                  {calendarProductivity.map((integration) => (
                    <IntegrationTile key={integration.id} integration={integration} t={t} />
                  ))}
                </div>
              </section>
            )}

            {/* ── Section 3 — Wearables & health ──────────────────── */}
            {wearablesHealth.length > 0 && (
              <section className="space-y-3">
                <SectionHeader label={t(SECTIONS[2].headerKey)} />
                <div className="space-y-2">
                  {wearablesHealth.map((integration) => (
                    <IntegrationTile key={integration.id} integration={integration} t={t} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </div><PeopleTaskActionModal open={!!peoplePrefill} prefill={peoplePrefill} onClose={() => { setPeoplePrefill(null); void refresh(); }} onCreated={() => void refresh()} />
    <Suspense fallback={<span role="status" className="sr-only">{t("actions.loading")}</span>}>
      {externalTaskPrefill && <ExternalTaskActionModal open prefill={externalTaskPrefill} onClose={() => {setExternalTaskPrefill(null);void refresh();}} onCreated={() => {void refresh();}} />}
      {contactPrefill && <ContactActionModal open prefill={contactPrefill} onClose={() => { setContactPrefill(null); void refresh(); }} onCreated={() => { void refresh(); }} />}
      {emailPrefill && <EmailActionModal open prefill={emailPrefill} onClose={() => { setEmailPrefill(null); void refresh(); }} onCreated={() => void refresh()} />}
      {fileEditOpen && connectionOwner && <ConnectedFileEditWorkspace ownerId={connectionOwner} onCancel={() => { setFileEditOpen(false); void refresh(); }} />}
      {filePrefill && <FileActionModal open prefill={filePrefill} allowManualFileWrites uploadOrigin={fileOrigin ?? undefined} onClose={() => { setFilePrefill(null); setFileOrigin(null); void refresh(); }} onCreated={() => void refresh()} />}
    </Suspense>
    </MotionConfig>
  );
}

// ── Section header ────────────────────────────────────────────────────
const SectionHeader: React.FC<{ label: string }> = ({ label }) => (
  <p
    className="text-[10px] font-display tracking-[0.18em] uppercase mb-2 px-1"
    style={{ color: "hsl(187 72% 50% / 0.65)" }}
  >
    {label}
  </p>
);

// ── Static integration tile ──────────────────────────────────────────
// Single-line affordance for non-live providers. Always shows the
// "Em breve" badge — no fake "Conectar" button that does nothing (the
// pre-refactor priority:true blue button was misleading for Apple Health
// in particular). When a provider goes live it moves out of INTEGRATIONS
// into the Active section as a real <ConnectionCard>.
const IntegrationTile: React.FC<{
  integration: IntegrationConfig;
  t: (key: string) => string;
}> = ({ integration, t }) => {
  const Icon = integration.icon;
  return (
    <div
      className="rounded-2xl p-4 flex items-center gap-3"
      style={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border) / 0.20)" }}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: integration.color + "15", border: "1px solid " + integration.color + "30" }}
      >
        <Icon size={17} style={{ color: integration.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-display text-[13px] font-semibold text-foreground">
          {t(integration.nameKey)}
        </p>
        <p className="text-[10px] font-body text-muted-foreground/50 mt-0.5 leading-snug">
          {t(integration.descriptionKey)}
        </p>
      </div>
      <div
        className="px-3 py-1.5 rounded-xl shrink-0"
        style={{ background: "hsl(222 18% 13%)", border: "1px solid hsl(var(--border) / 0.20)" }}
      >
        <span className="text-[10px] font-display text-muted-foreground/40">
          {t("settings.integrations.soon")}
        </span>
      </div>
    </div>
  );
};
